import { DOCUMENT, NgIf, NgStyle, NgClass } from '@angular/common';
import {Component, Inject, OnInit, ViewChild, DestroyRef, inject} from '@angular/core';
import { NgForm, NgModel, FormsModule } from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {CookieService} from 'ngx-cookie-service';
import {finalize} from 'rxjs/operators';
import {GtErrorResponse} from '../../../core/models/GtErrorResponse';
import {LOGO} from '../../../core/models/logo.enum';
import {Position} from '../../../core/models/Position';
import {TokenStatus} from '../../../core/models/TokenStatus';
import {AlertService} from '../../../core/services/alert.service';
import {LoginService} from '../../../core/services/login.service';
import {SessionStorageService} from '../../../core/services/session-storage.service';
import {UtilsService} from '../../../core/services/utils';
import {OAuthConstants} from '../../constant/oAuth.constants';
import {PasswordPolicy} from '../../models/reset-password/PasswordPolicy';
import {PasswordPolicyNote} from '../../models/reset-password/PasswordPolicyNote';
import {PasswordPolicyResponse} from '../../models/reset-password/PasswordPolicyResponse';
import {ResetForm} from '../../models/reset-password/ResetForm';
import {LoginChallengeDataService} from '../../../core/services/login-challenge-data.service';
import {DomainDataService} from '../../../core/services/domain-data.service';
import {encrypt} from '../../../utils/encrypt-util';
import { ResetPwdErrorStateComponent } from './error-state/reset-pwd-error-state.component';
import { EqualToDirective } from '../../directives/equal-to.directive';
import { GtLoadingComponent } from '../../../core/components/gt-loading/gt-loading.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
    selector: 'reset-password',
    templateUrl: 'reset-password.component.html',
    styleUrls: ['reset-password.component.scss'],
    imports: [NgIf, GtLoadingComponent, FormsModule, NgStyle, NgClass, EqualToDirective, ResetPwdErrorStateComponent]
})
export class ResetPasswordComponent implements OnInit {
  public resetPwdForm: ResetForm;
  public invalidTokenInfo: GtErrorResponse;
  public tokenStatus = TokenStatus;
  public passwordPolicy = <PasswordPolicy>{};
  public passwordPolicyNote;
  public domainName: string;
  public isSubmitted = false;
  public errorMessage: string;
  public isLoading = true;
  public clientLogo = LOGO.CLIENT;
  public isSubmitting = false;
  public emoji = '&#128522;';
  public checkListCount = 0;
  public currentCheckListCount = 0;
  private checkListPercentage = [];
  private checkCounter = [];

  @ViewChild('reset')
  private formGroup: NgForm;
  @ViewChild('newPassword')
  public newPasswordCtrl: NgModel;
  public showPolicy = false;
  private hidePolicyTimer: any;
  private destroyRef = inject(DestroyRef);

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _loginService: LoginService,
    private _utils: UtilsService,
    private cookieService: CookieService,
    private sessionStorage: SessionStorageService,
    @Inject(DOCUMENT) private document: any,
    private alert: AlertService,
    private _loginChallengeDataService: LoginChallengeDataService,
    private _domainDataService: DomainDataService
  ) {
    this._resetSuccessSubscriber = this._resetSuccessSubscriber.bind(this);
    this._resetErrorSubscriber = this._resetErrorSubscriber.bind(this);
    this._validToken = this._validToken.bind(this);
    this._invalidToken = this._invalidToken.bind(this);
    this._setPwdPolicy = this._setPwdPolicy.bind(this);
  }

  public ngOnInit() {
    this.resetPwdForm = <ResetForm>{};
    this._route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.loadRouteParams.bind(this));
    this._verifyToken(this.resetPwdForm.guid, this.domainName);
    this._loadPasswordPolicy();
  }

  public onSubmit() {
    this.isSubmitted = true;
    if (this.formGroup.invalid || this.isSubmitting) {
      return;
    }

    this.resetPwdForm.encNewPassword = encrypt(this.resetPwdForm.newPassword);
    this.resetPwdForm.encConfirmPassword = encrypt(this.resetPwdForm.confirmPassword);
    this.isSubmitting = true;
    this._loginService.resetPassword(this.resetPwdForm, this.domainName)
      .pipe(
        finalize(() => this.isSubmitting = false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this._resetSuccessSubscriber, this._resetErrorSubscriber);
  }

  public returnToLogin() {
    const domain = window.atob(this.domainName);
    const {protocol} = window.location;
    this.document.location.href = `${protocol}//${domain}${OAuthConstants.LOGIN_ROUTE}`;
  }

  public get logoPosition(): string {
    return this.invalidTokenInfo ? Position.CENTER : Position.LEFT;
  }

  public get getPassedChecksPercentage(): string {
    return this.checkListPercentage[this.currentCheckListCount - 1] || '0%';
  }

  public onDataChange(event: any): void {
    this.errorMessage = '';
    clearTimeout(this.hidePolicyTimer);
    this.showPolicy = true;

    this.checkCounter = [];

    if (!this.newPasswordCtrl?.hasError('required') && !this.newPasswordCtrl?.hasError('minlength') && this.newPasswordCtrl?.dirty) {
      this.checkCounter.push(true);
    }

    if (!this.newPasswordCtrl?.hasError('required') && !this.newPasswordCtrl?.hasError('maxlength') && this.newPasswordCtrl?.dirty) {
      this.checkCounter.push(true);
    }

    if (this.passwordPolicy.complexity || this.passwordPolicy.allowedSpecialChar) {
      if (!this.newPasswordCtrl?.hasError('required') && !this.newPasswordCtrl?.hasError('pattern') && this.newPasswordCtrl?.dirty) {
        this.checkCounter.push(true);
      }
    }

    this.currentCheckListCount = this.checkCounter.length;

    if (this.newPasswordCtrl && this.newPasswordCtrl.valid) {
      this.hidePolicyTimer = setTimeout(() => {
        this.showPolicy = false;
      }, 2000);
    }
  }

  public onPasswordFocus(): void {
    clearTimeout(this.hidePolicyTimer);
    this.showPolicy = true;
  }

  public onPasswordBlur(): void {
    clearTimeout(this.hidePolicyTimer);
    this.showPolicy = false;
  }

  private fillCheckList(): void {
    this.checkListCount = Object.keys(this.passwordPolicyNote || {}).length < 3 ? Object.keys(this.passwordPolicyNote || {}).length : 3;
    const initialValue = 100 / this.checkListCount;
    this.checkListPercentage = Array.from(new Array(this.checkListCount)).map((e, i) => {
      const percentage = initialValue * (i + 1);
      return percentage + '%';
    });
  }

  private loadRouteParams(params: Params) {
    this.resetPwdForm.guid = params.guid;
    this.domainName = params.domain;
    this._domainDataService.setDomain(this.domainName);
  }

  private _resetSuccessSubscriber(res) {
    const {redirectUrl} = res;
    const param = this._utils.getQueryString('guid', redirectUrl);
    this._loginChallengeDataService.setLoginChallenge(param);
    if (param) {
      this.document.location.href = redirectUrl;
      return;
    }
    this._router.navigate([redirectUrl]);
  }

  private _resetErrorSubscriber(err) {
    if (!err) {
      return;
    }
    const {error} = err;
    this.errorMessage = error.message;
  }

  private _verifyToken(token: string, domain: string) {
    this._loginService.validateToken(token, domain)
      .pipe(
        finalize(() => this.isLoading = false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this._validToken, this._invalidToken);
  }

  private _loadPasswordPolicy() {
    this._loginService.getPasswordPolicy(this.domainName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this._setPwdPolicy);
  }

  private _validToken(res) {
  }

  private _invalidToken(err) {
    const {error} = err;
    if (!error) {
      return;
    }
    error.status = err.status;
    this.invalidTokenInfo = error;
  }

  private _setPwdPolicy({passwordPolicy, passwordPolicyNotes}: PasswordPolicyResponse) {
    if (passwordPolicy.complexity) {
      this.passwordPolicy.pwdRegex = this._getPwdRegex(passwordPolicy);
    }
    this.passwordPolicy = {...passwordPolicy, ...this.passwordPolicy};
    this.passwordPolicyNote = this._preparePolicyNote(passwordPolicyNotes, passwordPolicy);
    this.fillCheckList();
  }

  private _getPwdRegex({allowedSpecialChar}: PasswordPolicy): RegExp {
    const symbols = `[${allowedSpecialChar.replace(',', '')}]`;
    return new RegExp(symbols);
  }

  private _preparePolicyNote(policyNotes: PasswordPolicyNote[], policyParams: PasswordPolicy) {
    return policyNotes.reduce((model, note) => {
      const replacementObj = Object.keys(policyParams).find((param) => param === note.optKey);
      const description = this._utils.parseTemplateString(note.description, policyParams, '[', ']');
      if (policyParams[replacementObj]) {
        model[replacementObj] = description;
      }
      return {...model};
    }, <PasswordPolicyNote>{});
  }


}
